import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  Snackbar,
  CircularProgress,
  AlertTitle,Slide, LinearProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { endpoint } from "../services/endpoint";
import { login } from '../redux/slices/authSlice';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import Lottie from "lottie-react";
import meetingAnimation from "../assets/meeting-analytic.json";
import MuiAlert from "@mui/material/Alert";
import ErrorIconImage from "../assets/ErrorIconImage.png";

const slideIn = keyframes`
  0% {
    opacity: 0;
    transform: translateX(50%);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Alert = React.forwardRef((props, ref) => (
  <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />
));

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleLogin = async () => {
    setLoading(true);

    const payload = { userName: email, password };

    try {
      const response = await axios.post(endpoint.candidate_login, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      const userData = response?.data;
      dispatch(login(userData));
      localStorage.setItem("examDetails", JSON.stringify(userData));
      localStorage.setItem('userRole', userData?.role === 'admin' ? 'admin' : 'visitor');

      setSnackbar({
        open: true,
        message: "Login successful!",
        severity: "success",
      });

      setTimeout(() => navigate("/dashboard"), 1000); // small delay to show snackbar
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Invalid username or password",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => setOpenDialog(true);
  const handleCloseDialog = () => setOpenDialog(false);
  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: "#f0f2f5",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          height: "100%",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Left Side */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            height: "100%",
            padding: { xs: 2, md: 1 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            background: "#fdfcfc",
          }}
        >
          <Card
            sx={{
              maxWidth: 400,
              width: "100%",
              boxShadow: 0,
              borderRadius: 3,
              background: "transparent",
              animation: `${slideIn} 0.5s ease-out`,
              padding: { xs: 2, md: 1 },
            }}
          >
            <CardContent>
              <Typography
                variant="h4"
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  fontWeight: "bold",
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  lineHeight: 1.2,
                  marginBottom: 3,
                  color: "#333",
                }}
              >
                Croire Insights Portal – Sign In to Continue
              </Typography>

              <Box component="form" noValidate autoComplete="off">
                <TextField
                  label="Username"
                  placeholder="Enter your username"
                  variant="standard"
                  fullWidth
                  margin="normal"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputLabelProps={{ sx: { color: "#666", fontSize: "1rem" } }}
                  InputProps={{
                    sx: { fontSize: "1rem", paddingTop: '8px' },
                  }}
                  sx={{
                    '& .MuiInput-underline:before': { borderBottomColor: '#ccc' },
                    '& .MuiInput-underline:after': { borderBottomColor: '#333' },
                  }}
                />

                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  variant="standard"
                  fullWidth
                  margin="normal"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputLabelProps={{ sx: { color: "#666", fontSize: "1rem" } }}
                  InputProps={{
                    sx: { fontSize: "1rem", paddingTop: '8px' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePasswordVisibility} edge="end" sx={{ color: '#666' }}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{ autoComplete: 'new-password' }}
                  sx={{
                    marginTop: 3,
                    '& .MuiInput-underline:before': { borderBottomColor: '#ccc' },
                    '& .MuiInput-underline:after': { borderBottomColor: '#333' },
                  }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      sx={{
                        color: "#666",
                        '&.Mui-checked': { color: "#333" },
                      }}
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontSize: "0.9rem", color: "#666" }}>Remember me</Typography>}
                  sx={{ marginTop: 2 }}
                />

                <Box sx={{ marginTop: 4, display: "flex", justifyContent: "center" }}>
                  {loading ? (
                    <CircularProgress size={28} color="primary" />
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleLogin}
                      sx={{
                        width: "50%",
                        padding: "10px 0",
                        fontSize: "1.1rem",
                        borderRadius: 8,
                        backgroundColor: "#0a77fe",
                        color: "white",
                        '&:hover': {
                          backgroundColor: "#4f9eff",
                        },
                        boxShadow: 'none',
                        textTransform: 'none',
                      }}
                    >
                      Sign in
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Right Side with Lottie */}
        <Box
          sx={{
            width: { xs: "100%", md: "50%" },
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fdfcfc",
            borderBottomRightRadius: { xs: "16px", md: "16px" },
            borderTopRightRadius: { xs: "0", md: "16px" },
          }}
        >
          <Lottie
            animationData={meetingAnimation}
            loop={true}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
      </Box>

      {/* Forgot Password Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} sx={{ "& .MuiDialog-paper": { borderRadius: "16px" } }}>
        <DialogTitle sx={{ backgroundColor: "red", color: "white", marginBottom: 1.5 }}>Forgot Password?</DialogTitle>
        <DialogContent>
          <Typography>Please contact your Admin.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Message */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={(props) => <Slide {...props} direction="left" />}
      >
      <Box
        sx={{
          minWidth: 300,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: 3,
          bgcolor:
            snackbar.severity === "success"
              ? "#fdfcfc"
              : snackbar.severity === "error"
              ? "#fdfcfc"
              : "#fdfcfc",
          // borderLeft: `6px solid ${
          //   snackbar.severity === "success"
          //     ? "#2e7d32"
          //     : snackbar.severity === "error"
          //     ? "#d32f2f"
          //     : "#ed6c02"
          // }`,
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 0,
            pr: 0,
          }}
        >
          <Alert
            icon={false}
            severity={snackbar.severity}
            sx={{
              bgcolor: "#fdfcfc",
              color: "#333",
              p: 1,
              width: "100%",
              fontSize: 15,
              "& .MuiAlert-icon": {
                fontSize: 28,
              },
            }}
          >
            <AlertTitle sx={{ mb: 0, fontWeight: " " }}>
              {snackbar.severity === "success" && "✅ Successfully Signed In"}
              {snackbar.severity === "error" && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <img
                      src={ErrorIconImage}
                      alt="Error Icon"
                      style={{ width: 24, height: 24 }}
                    />
                    <span>{snackbar.message}</span>
                  </Box>
                )}
              {/* {snackbar.severity === "error" && "🚫 Invalid credentials"} */}
              {snackbar.severity === "warning" && "⚠️ Invalid input, check again"}
            </AlertTitle>
          </Alert>

          <IconButton
            size="small"
            onClick={handleSnackbarClose}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Custom underline */}
        <LinearProgress
          variant="determinate"
          value={100}
          sx={{
            height: 2,
            bgcolor: "#ddd",
            "& .MuiLinearProgress-bar": {
              backgroundColor:
                snackbar.severity === "success"
                  ? "#2e7d32"
                  : snackbar.severity === "error"
                  ? "#d32f2f"
                  : "#ed6c02",
            },
          }}
        />
      </Box>
      </Snackbar>

    </Box>
  );
};

export default LoginPage;
